import React, { useState, useContext, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { AuthContext } from "../../../context/AuthContext";
import PersonalDetails from "../Comman/PersonalForm/PersonalDetails";
import ContactInformation from "../Comman/PersonalForm/ContactInformation";
import EmergencyContact from "../Comman/PersonalForm/EmergencyContact";
import Loading from "../../Loading";
import API from "../../../services/interceptor";

const AdminReceptionistPersonalProfile = () => {
  const {
    loginUser,
    currentUserProfile,
    currentUserProfileLoading,
    currentUserProfileError,
    getCurrentUserProfile,
  } = useContext(AuthContext);

  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Initial data loading
  useEffect(() => {
    if (loginUser?.id && !currentUserProfile) {
      getCurrentUserProfile(loginUser.id);
    }
  }, [loginUser, currentUserProfile, getCurrentUserProfile]);

  // Update formData when currentUserProfile changes
  useEffect(() => {
    if (currentUserProfile?.userDetails) {
      const userDetailsData = currentUserProfile.userDetails;
      setOriginalData(userDetailsData);
      setFormData(userDetailsData);
    }
  }, [currentUserProfile]);

  // Detect changes between formData and originalData
  useEffect(() => {
    const isChanged = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasChanges(isChanged);
  }, [formData, originalData]);

  // Handle form input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const keys = name.split(".");

    setFormData((prevData) => {
      let updatedData = { ...prevData };
      let current = updatedData;

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          current[key] = value;
        } else {
          current[key] = { ...current[key] };
          current = current[key];
        }
      });
      return updatedData;
    });
  }, []);

  // API call to update user details
  const updateData = async (updatedData) => {
    try {
      setUpdateLoading(true);
      const response = await API.put(
        `/api/users/details/${loginUser.id}`,
        updatedData
      );
      setOriginalData(updatedData); // Update original data to reflect changes
      getCurrentUserProfile(loginUser.id); // Refresh the profile data
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to update user details.");
    } finally {
      setUpdateLoading(false);
    }
  };

  // Submit the form
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!hasChanges) {
        alert("No changes detected.");
        return;
      }

      try {
        await updateData(formData);
        alert("User details updated successfully.");
      } catch (err) {
        console.error("Error updating user details:", err);
        alert(err.message || "Failed to update user details.");
      }
    },
    [formData, hasChanges, updateData]
  );

  // Reset form to original data
  const handleReset = useCallback(() => {
    setFormData(originalData);
  }, [originalData]);

  if (currentUserProfileLoading) return <Loading />;
  
  if (currentUserProfileError) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md mx-auto mt-8">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Profile</h2>
          <p className="text-gray-600 mb-4">{currentUserProfileError}</p>
          <button
            onClick={() => loginUser?.id && getCurrentUserProfile(loginUser.id)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!currentUserProfile?.userDetails) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md mx-auto mt-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Profile Data</h2>
          <p className="text-gray-600 mb-4">Your profile information is not available.</p>
          <button
            onClick={() => loginUser?.id && getCurrentUserProfile(loginUser.id)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Load Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      className="p-6 bg-white rounded-lg shadow-md mx-auto mt-8"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Personal Details
        </h2>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            loginUser?.role === 'admin' 
              ? 'bg-red-100 text-red-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {loginUser?.role?.toUpperCase()}
          </div>
          {hasChanges && (
            <div className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              Unsaved Changes
            </div>
          )}
        </div>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <PersonalDetails formData={formData} onChange={handleChange} />
        <ContactInformation formData={formData} onChange={handleChange} />
        <EmergencyContact formData={formData} onChange={handleChange} />
        
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            {hasChanges && (
              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-2 rounded-lg text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
              >
                Reset Changes
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={!hasChanges || updateLoading}
              className={`px-6 py-2 rounded-lg text-white flex items-center space-x-2 ${
                hasChanges && !updateLoading
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "bg-gray-300 cursor-not-allowed"
              } transition-colors duration-200`}
            >
              {updateLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{updateLoading ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </div>
      </form>

      {/* Account Information */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <p className="text-gray-900">{loginUser?.email}</p>
            <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Role
            </label>
            <p className="text-gray-900 capitalize">{loginUser?.role}</p>
            <p className="text-xs text-gray-500 mt-1">Assigned by administrator</p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Created
            </label>
            <p className="text-gray-900">
              {currentUserProfile?.createdAt ? 
                new Date(currentUserProfile.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 
                'Not available'
              }
            </p>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User ID
            </label>
            <p className="text-gray-900 font-mono text-sm">
              {loginUser?.id?.slice(-12) || 'Not available'}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminReceptionistPersonalProfile;